/**
 * EN-11178: table frames that arrive OUT OF ORDER -- counted always, and
 * dropped behind `release.table-frame-order`.
 *
 * Each table update is its own server-side delivery (EventBridge -> broadcast
 * Lambda): a cold start or a retry can land an older frame after a newer one.
 * Every frame now carries `frameVersion` -- the time, in microseconds on the
 * database clock, of the read that built it
 * (serverless-v2/shared/config/frameVersion.js). A later read shows a state at
 * least as new, so a frame whose version is not above the newest one already
 * seen for its table shows a state that table has already left.
 *
 * Frames are taken in ARRIVAL order, before compaction. Detection there counts
 * a late frame whether or not compaction would have hidden it. Dropping there
 * means every frame that survives is newer than all before it, so compaction
 * and the background buffer -- both "last arrival wins" -- keep the newest
 * without knowing about versions.
 *
 * A dropped frame takes its Table History row with it, as the reducer's
 * `version` guard already does for its frames: the row describes a state the
 * table has left, and appending it would put it out of order.
 *
 * Never a frozen table, and never a stale frame let through to prevent one: the
 * valve opens only for a frame that reads older than the mark yet shows the
 * table further along (isLaterState). A late delivery cannot do that -- the
 * mark's read saw the table in its state, so any state after it was written,
 * and read, later -- so such a frame means the mark itself is wrong: the
 * database clock stepped back (a writer failover, say). That frame is applied
 * and becomes the mark. Late frames from the same or an earlier state are always
 * dropped, however many arrive. After a clock step the table waits at most until
 * its state next moves on.
 *
 * Unversioned frames (a producer without the stamp) are always applied and
 * leave the mark alone. Not `version`: the reducer already drops frames on that.
 */

/** Where a table's newest frame got to. */
interface FrameMark {
  frameVersion: number;
  gameNo: string;
  hand: string;
}

/** An out-of-order frame: not newer than the newest one already seen for its table. */
export interface OutOfOrderFrame {
  gameID: string;
  /** How far behind the newest frame it is, in ms of read time (0 for a repeat). */
  behindMs: number;
  /** Whether it is from an earlier hand than the newest frame: a flashback across hands. */
  crossHand: boolean;
  /** Whether it was dropped rather than applied. */
  dropped: boolean;
  frame: { gameNo: string; hand: string };
  newest: { gameNo: string; hand: string };
}

/** The valve opening: an older-reading frame showed a later state, so the mark was reset. */
export interface FrameOrderValve {
  gameID: string;
  /** Frames dropped in a row before this one was let through. */
  dropped: number;
  /** How far behind the old mark the frame let through was, in ms of read time. */
  behindMs: number;
}

export interface FrameOrderStats {
  /** Versioned frames checked for this table. */
  checked: number;
  /** Frames older than the newest seen (repeats of the newest are not counted). */
  outOfOrder: number;
  crossHand: number;
  worstBehindMs: number;
  /** Frames not applied: late ones and repeats, with release.table-frame-order on. */
  dropped: number;
  valveOpened: number;
  last: OutOfOrderFrame | null;
}

/** Tables kept; the oldest-seen is forgotten past this. A session has a handful open. */
const MAX_TABLES = 64;

/**
 * A hand state's place in its hand. Case "1" moves the button and bumps gameNo
 * as it writes hand 2, so within one gameNo the order is 2 … 16, then '', 0 and
 * 1 (the housekeeping that follows it). Null for a state outside that order.
 */
const stateOrder = (hand: string): number | null => {
  if (hand === '') return 17;
  if (hand === '0') return 18;
  if (hand === '1') return 19;
  const n = Number(hand);
  return Number.isInteger(n) && n >= 2 && n <= 16 ? n : null;
};

/** Whether `frame` shows its table further along than `mark`: a later hand, or a later state of it. */
export const isLaterState = (
  frame: { gameNo: string; hand: string },
  mark: { gameNo: string; hand: string }
): boolean => {
  const frameNo = Number(frame.gameNo);
  const markNo = Number(mark.gameNo);
  if (
    frame.gameNo === '' ||
    mark.gameNo === '' ||
    !Number.isFinite(frameNo) ||
    !Number.isFinite(markNo)
  ) {
    return false;
  }
  if (frameNo !== markNo) return frameNo > markNo;
  const frameState = stateOrder(frame.hand);
  const markState = stateOrder(mark.hand);
  return frameState !== null && markState !== null && frameState > markState;
};

const newestByTable = new Map<string, FrameMark>();
/** Frames dropped in a row per table, for the valve report. */
const dropsInARow = new Map<string, number>();
const statsByTable = new Map<string, FrameOrderStats>();
const listeners = new Set<() => void>();

const emptyStats = (): FrameOrderStats => ({
  checked: 0,
  outOfOrder: 0,
  crossHand: 0,
  worstBehindMs: 0,
  dropped: 0,
  valveOpened: 0,
  last: null
});

/** `frameVersion` as a positive safe integer, or null for a frame that carries none. */
export const readFrameVersion = (payload: any): number | null => {
  const value = Number(payload?.frameVersion);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
};

const remember = <V>(map: Map<string, V>, key: string, value: V) => {
  map.delete(key);
  map.set(key, value);
  if (map.size > MAX_TABLES) map.delete(map.keys().next().value as string);
};

interface FrameVerdict {
  admit: boolean;
  outOfOrder: OutOfOrderFrame | null;
  valve: FrameOrderValve | null;
}

const ADMIT: FrameVerdict = { admit: true, outOfOrder: null, valve: null };

const judgeFrame = (payload: any, dropStale: boolean): FrameVerdict => {
  const gameID = String(payload?.game?.gameID ?? '');
  const frameVersion = readFrameVersion(payload);
  if (!gameID || gameID === '0' || frameVersion === null) return ADMIT;

  const mark: FrameMark = {
    frameVersion,
    gameNo: String(payload.game.gameNo ?? ''),
    hand: String(payload.game.hand ?? '')
  };
  const stats = statsByTable.get(gameID) ?? emptyStats();
  const newest = newestByTable.get(gameID);

  let verdict: FrameVerdict = ADMIT;
  if (!newest || frameVersion > newest.frameVersion) {
    remember(newestByTable, gameID, mark);
    dropsInARow.delete(gameID);
  } else {
    const late = frameVersion < newest.frameVersion;
    const valveOpens = dropStale && isLaterState(mark, newest);
    const drop = dropStale && !valveOpens;
    const behindMs = (newest.frameVersion - frameVersion) / 1000;
    const droppedBefore = dropsInARow.get(gameID) ?? 0;

    if (valveOpens) {
      remember(newestByTable, gameID, mark);
      dropsInARow.delete(gameID);
    } else if (drop) {
      remember(dropsInARow, gameID, droppedBefore + 1);
    }

    verdict = {
      admit: !drop,
      // A repeat of the newest frame is not out of order, only redundant.
      outOfOrder: late
        ? {
            gameID,
            behindMs,
            crossHand: mark.gameNo !== newest.gameNo,
            dropped: drop,
            frame: { gameNo: mark.gameNo, hand: mark.hand },
            newest: { gameNo: newest.gameNo, hand: newest.hand }
          }
        : null,
      valve: valveOpens ? { gameID, dropped: droppedBefore, behindMs } : null
    };
  }

  const { outOfOrder } = verdict;
  // A new object on every change, so useSyncExternalStore sees it.
  remember(statsByTable, gameID, {
    checked: stats.checked + 1,
    outOfOrder: stats.outOfOrder + (outOfOrder ? 1 : 0),
    crossHand: stats.crossHand + (outOfOrder?.crossHand ? 1 : 0),
    worstBehindMs: Math.max(stats.worstBehindMs, outOfOrder?.behindMs ?? 0),
    dropped: stats.dropped + (verdict.admit ? 0 : 1),
    valveOpened: stats.valveOpened + (verdict.valve ? 1 : 0),
    last: outOfOrder ?? stats.last
  });
  listeners.forEach((listener) => listener());
  return verdict;
};

/**
 * Check one decompressed table frame against the newest seen for its table,
 * without dropping anything. Returns the out-of-order frame it is, or null (in
 * order, a repeat of the newest, or unversioned).
 */
export const checkFrameOrder = (payload: any): OutOfOrderFrame | null =>
  judgeFrame(payload, false).outOfOrder;

export interface AdmitTableFramesOptions {
  /** release.table-frame-order: drop frames not newer than their table's newest. */
  dropStale: boolean;
  /** Each out-of-order frame (the Sentry report, when ops.frame-order-report is on). */
  onOutOfOrder?: (frame: OutOfOrderFrame) => void;
  /** Each valve opening (reported the same way). */
  onValveOpened?: (valve: FrameOrderValve) => void;
}

/**
 * Take a batch of decompressed frames in the order they ARRIVED and return the
 * ones to apply, in that order. Call it before compaction (see the top of this
 * file). With `dropStale` off it returns every frame, and only counts.
 */
export const admitTableFrames = <Frame>(
  frames: readonly Frame[],
  { dropStale, onOutOfOrder, onValveOpened }: AdmitTableFramesOptions
): Frame[] =>
  frames.filter((frame) => {
    const { admit, outOfOrder, valve } = judgeFrame(frame, dropStale);
    if (outOfOrder) onOutOfOrder?.(outOfOrder);
    if (valve) onValveOpened?.(valve);
    return admit;
  });

/** This session's counts for one table; a new object each time they change. */
export const readFrameOrderStats = (gameID: string): FrameOrderStats | undefined =>
  statsByTable.get(gameID);

/** For useSyncExternalStore: called whenever any table's counts change. */
export const subscribeFrameOrder = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Tests only. */
export const resetFrameOrder = () => {
  newestByTable.clear();
  dropsInARow.clear();
  statsByTable.clear();
};
